// Hooks for alerts, air quality and the weather-nerd data. One shared query
// per key, so any number of widgets on any screen share one request.

import type { AirPayload } from "../../shared/air";
import type { AlertsPayload, WarningShape } from "../../shared/models";
import type { GroundPayload, RiversPayload, SpotsPayload } from "../../shared/outdoors";
import type { DiscussionPayload, GridPayload, ObsDetail, OutlookPayload } from "../../shared/weatherDetail";
import { useHousehold } from "../app/useHousehold";
import { useSkyQuery } from "../widgets/sky/api";

const MIN = 60_000;

function useLoc() {
  const loc = useHousehold().settings.location;
  return { loc, key: loc ? `${loc.lat},${loc.lon}` : "none" };
}

/** Active alerts; polled every 2 minutes so the Alerts screen reacts quickly. */
export function useAlerts(enabled = true) {
  const { loc, key } = useLoc();
  return useSkyQuery<AlertsPayload>(["wx", "alerts", key], "/weather/alerts", { refetchMs: 2 * MIN, staleMs: MIN, enabled: enabled && Boolean(loc) });
}

export function useWarningShapes(enabled = true) {
  const { loc, key } = useLoc();
  return useSkyQuery<WarningShape[]>(["wx", "warnings", key], "/weather/warnings", { refetchMs: 3 * MIN, staleMs: 2 * MIN, enabled: enabled && Boolean(loc) });
}

export function useGrid() {
  const { loc, key } = useLoc();
  return useSkyQuery<GridPayload>(["wx", "grid", key], "/weather/grid", { refetchMs: 30 * MIN, staleMs: 15 * MIN, enabled: Boolean(loc) });
}

export function useObsDetail(station: string | null) {
  return useSkyQuery<ObsDetail>(["wx", "obs-detail", station], `/weather/obs-detail?station=${station ?? ""}`, { refetchMs: 10 * MIN, staleMs: 5 * MIN, enabled: Boolean(station) });
}

export function useOutlook() {
  const { loc, key } = useLoc();
  return useSkyQuery<OutlookPayload>(["wx", "outlook", key], "/weather/outlook", { refetchMs: 30 * MIN, staleMs: 15 * MIN, enabled: Boolean(loc) });
}

export function useDiscussion() {
  const { loc, key } = useLoc();
  return useSkyQuery<DiscussionPayload>(["wx", "discussion", key], "/weather/discussion", { refetchMs: 30 * MIN, staleMs: 15 * MIN, enabled: Boolean(loc) });
}

export function useRivers() {
  const settings = useHousehold().settings;
  const key = `${settings.location ? `${settings.location.lat},${settings.location.lon}` : "none"}|${settings.riverSites.join(",")}`;
  return useSkyQuery<RiversPayload>(["outdoors", "rivers", key], "/outdoors/rivers", { refetchMs: 15 * MIN, staleMs: 10 * MIN, enabled: Boolean(settings.location) || settings.riverSites.length > 0 });
}

export function useSpots() {
  const spots = useHousehold().settings.outdoorSpots;
  const key = spots.map((s) => `${s.id}@${s.lat},${s.lon}`).join("|");
  return useSkyQuery<SpotsPayload>(["outdoors", "spots", key], "/outdoors/spots", { refetchMs: 30 * MIN, staleMs: 15 * MIN, enabled: spots.length > 0 });
}

/** The home station's last three days of rain, for trail conditions. */
export function useGround(station: string | null) {
  return useSkyQuery<GroundPayload>(["outdoors", "ground", station], `/outdoors/ground?station=${station ?? ""}`, { refetchMs: 30 * MIN, staleMs: 15 * MIN, enabled: Boolean(station) });
}

export function useAir() {
  const { loc, key } = useLoc();
  return useSkyQuery<AirPayload>(["wx", "air", key], "/weather/air", { refetchMs: 30 * MIN, staleMs: 15 * MIN, enabled: Boolean(loc) });
}
